// Tiny markdown renderer for the Tap In News article body.
// Intentionally minimal — supports headings, paragraphs, bold/italic,
// inline code, links, blockquotes, ordered/unordered lists, images, and
// horizontal rules. Escapes HTML by default so admin-pasted content can
// never inject scripts even if Markdown is malformed.

function esc(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function inline(s) {
  let out = esc(s);
  // images ![alt](url)
  out = out.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_m, alt, url) =>
    `<img src="${esc(url)}" alt="${esc(alt)}" class="rounded-xl my-4 w-full" />`
  );
  // links [text](url)
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g,
    (_m, t, u) => `<a href="${esc(u)}" target="_blank" rel="noopener noreferrer" class="text-tv-accent underline hover:text-white">${esc(t)}</a>`
  );
  // bold **x**
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // italic *x*
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
  // inline code `x`
  out = out.replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 bg-white/10 rounded text-sm">$1</code>');
  return out;
}

export function renderMarkdown(md) {
  if (!md) return "";
  const lines = String(md).replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // horizontal rule
    if (/^---+\s*$/.test(line)) { out.push("<hr class='border-white/10 my-6' />"); i++; continue; }
    // headings
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1].length;
      const sizes = { 1: "text-3xl", 2: "text-2xl", 3: "text-xl", 4: "text-lg" };
      out.push(`<h${level} class="font-display font-black ${sizes[level]} text-white mt-8 mb-3">${inline(h[2])}</h${level}>`);
      i++; continue;
    }
    // blockquote
    if (/^>\s/.test(line)) {
      const buf = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      out.push(`<blockquote class="border-l-4 border-tv-accent pl-4 py-1 italic text-white/80 my-4">${inline(buf.join(" "))}</blockquote>`);
      continue;
    }
    // unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i++;
      }
      out.push("<ul class='list-disc pl-6 space-y-1 my-3'>" + items.map((x) => `<li>${inline(x)}</li>`).join("") + "</ul>");
      continue;
    }
    // ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i++;
      }
      out.push("<ol class='list-decimal pl-6 space-y-1 my-3'>" + items.map((x) => `<li>${inline(x)}</li>`).join("") + "</ol>");
      continue;
    }
    // blank
    if (!line.trim()) { i++; continue; }
    // paragraph (collapse continuation lines)
    const buf = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !/^(#|>|---|\s*[-*]\s+|\s*\d+\.\s+)/.test(lines[i])) {
      buf.push(lines[i]);
      i++;
    }
    out.push(`<p class="text-white/85 leading-relaxed my-3">${inline(buf.join(" "))}</p>`);
  }
  return out.join("\n");
}
